import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      // Sticky visibility is controlled only by the main Buy Now area.
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#abortController.abort();

    document.getElementById(
      'horizon-final-footer-space'
    )?.remove();

    document.querySelector(
      'footer, #shopify-section-footer, .footer'
    )?.style.removeProperty('padding-bottom');
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const { signal } = this.#abortController;

    const updateVisibility = () => {
      if (signal.aborted) return;

      const rect = buyButtonsBlock.getBoundingClientRect();

      // Show only after the main purchase area has completely left
      // the viewport above the page. Hide while it is visible.
      if (rect.bottom <= 0) {
        if (!this.#isStuck) this.#showStickyBar();
      } else if (this.#isStuck) {
        this.#hideStickyBar();
      }
    };

    this.#buyButtonsIntersectionObserver = new IntersectionObserver(
      () => updateVisibility(),
      { threshold: [0, 0.01, 1] }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);

    let ticking = false;
    const onScrollOrResize = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        updateVisibility();
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScrollOrResize, {
      passive: true,
      signal,
    });

    window.addEventListener('resize', onScrollOrResize, {
      passive: true,
      signal,
    });

    this.#targetAddToCartButton =
      productForm.querySelector('[ref="addToCartButton"]');

    this.#injectCustomStyles();
    this.#enhanceStickyBar();
    this.#updateBuyNowAnimation();
    this.#updateFooterScrollSpace();

    requestAnimationFrame(updateVisibility);
    window.setTimeout(updateVisibility, 250);
    window.setTimeout(updateVisibility, 800);
  }

  // Public action handlers
  /**
   * Handles the ORDER NOW button click in the sticky bar.
   * It no longer redirects to checkout. It scrolls the page up to the main
   * Buy Now / express checkout button (PenguinCOD), so the customer clicks the
   * real button and the store's own form opens.
   *
   * @param {Event} [event]
   */
  handleAddToCartClick = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    this.#scrollToMainBuyNow();
  };


  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }

        this.#enhanceStickyBar();
        this.#updateBuyNowAnimation();
        this.#updateFooterScrollSpace();

        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    if (!stickyBar) return;

    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';

    requestAnimationFrame(() => {
      this.#updateFooterScrollSpace();
      this.#updateBuyNowAnimation();
    });
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    if (!stickyBar) return;

    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';

    this.#updateFooterScrollSpace();
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */

  #injectCustomStyles() {
    if (document.getElementById('horizon-sticky-cart-final-styles')) return;

    const style = document.createElement('style');
    style.id = 'horizon-sticky-cart-final-styles';

    style.textContent = `
      /* =========================================================
         HORIZON CUSTOM STICKY CART
         ========================================================= */

      .sticky-add-to-cart__bar.horizon-final-sticky-cart {
        position: fixed !important;
        left: 50% !important;
        bottom: max(20px, var(--safe-area-inset-bottom, 0px)) !important;
        transform: translateX(-50%) translateY(calc(100% + 40px)) !important;
        z-index: 999999 !important;
        width: min(1180px, calc(100vw - 28px)) !important;
        max-width: 1180px !important;
        min-height: 0 !important;
        box-sizing: border-box !important;
        padding: 0 15px 11px !important;
        display: grid !important;
        grid-template-columns: 62px minmax(0, 1fr) auto !important;
        grid-template-rows: 40px auto !important;
        column-gap: 11px !important;
        row-gap: 10px !important;
        align-items: center !important;
        border-radius: 17px !important;
        background: #fff !important;
        color: #111 !important;
        opacity: 0 !important;
        visibility: hidden !important;
        overflow: hidden !important;
        box-shadow:
          0 4px 24px rgb(0 0 0 / 16%),
          0 1px 5px rgb(0 0 0 / 8%) !important;
      }

      .sticky-add-to-cart__bar.horizon-final-sticky-cart[data-stuck='true'] {
        transform: translateX(-50%) translateY(0) !important;
        opacity: 1 !important;
        visibility: visible !important;
      }

      .horizon-final-sticky-offer {
        grid-column: 1 / -1 !important;
        grid-row: 1 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        min-height: 40px !important;
        margin: 0 -15px !important;
        padding: 7px 14px !important;
        box-sizing: border-box !important;
        background: linear-gradient(
          90deg,
          #202020 0%,
          #292929 45%,
          #8c0000 100%
        ) !important;
        color: #fff !important;
        font-size: 13px !important;
        line-height: 1.25 !important;
        font-weight: 700 !important;
        text-align: center !important;
      }

      /* Image */
      .sticky-add-to-cart__bar.horizon-final-sticky-cart
      .sticky-add-to-cart__image {
        grid-column: 1 !important;
        grid-row: 2 !important;
        width: 62px !important;
        height: 62px !important;
        min-width: 62px !important;
        max-width: 62px !important;
        border-radius: 9px !important;
        overflow: hidden !important;
        background: #f5f5f5 !important;
      }

      .sticky-add-to-cart__bar.horizon-final-sticky-cart
      .sticky-add-to-cart__image-img {
        display: block !important;
        width: 100% !important;
        height: 100% !important;
        object-fit: cover !important;
      }

      /* Title is directly beside image and above prices */
      .sticky-add-to-cart__bar.horizon-final-sticky-cart
      .sticky-add-to-cart__info {
        grid-column: 2 !important;
        grid-row: 2 !important;
        min-width: 0 !important;
        align-self: center !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: center !important;
        gap: 6px !important;
        padding: 0 !important;
      }

      .sticky-add-to-cart__bar.horizon-final-sticky-cart
      .sticky-add-to-cart__title {
        display: block !important;
        margin: 0 !important;
        color: #111 !important;
        font-size: 16px !important;
        line-height: 1.2 !important;
        font-weight: 700 !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
      }

      .sticky-add-to-cart__bar.horizon-final-sticky-cart
      .sticky-add-to-cart__variant {
        display: none !important;
      }

      /* The native Horizon price box stays in place for Horizon's JS,
         but our visible price row is rendered inside the title/info column. */
      .sticky-add-to-cart__bar.horizon-final-sticky-cart
      .sticky-add-to-cart__price {
        display: none !important;
      }

      .horizon-final-sticky-price-row {
        display: flex !important;
        align-items: baseline !important;
        justify-content: flex-start !important;
        flex-wrap: nowrap !important;
        gap: 7px !important;
        min-width: 0 !important;
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden !important;
        white-space: nowrap !important;
      }

      .horizon-final-sticky-current-price,
      .horizon-final-sticky-compare-price,
      .horizon-final-sticky-discount {
        flex: 0 0 auto !important;
        white-space: nowrap !important;
      }

      .sticky-add-to-cart__bar.horizon-final-sticky-cart
      .sticky-add-to-cart__price
      .price-item__group,
      .sticky-add-to-cart__bar.horizon-final-sticky-cart
      .sticky-add-to-cart__price
      .price {
        margin: 0 !important;
        padding: 0 !important;
      }

      /* Actual selling price */
      .horizon-final-sticky-current-price {
        color: #111 !important;
        font-size: 17px !important;
        line-height: 1 !important;
        font-weight: 800 !important;
      }

      /* Compare-at price - always visibly slashed */
      .horizon-final-sticky-compare-price {
        color: #a8a8a8 !important;
        font-size: 13px !important;
        line-height: 1 !important;
        font-weight: 500 !important;
        text-decoration: line-through !important;
        text-decoration-thickness: 1px !important;
        text-decoration-color: #a8a8a8 !important;
      }

      .horizon-final-sticky-discount {
        color: #16833a !important;
        font-size: 12px !important;
        line-height: 1 !important;
        font-weight: 800 !important;
      }

      /* Sticky ORDER NOW */
      .sticky-add-to-cart__bar.horizon-final-sticky-cart
      .sticky-add-to-cart__button {
        grid-column: 3 !important;
        grid-row: 2 !important;
        width: 185px !important;
        min-width: 185px !important;
        height: 46px !important;
        padding: 0 27px !important;
        border: 0 !important;
        border-radius: 8px !important;
        background: #FD7911 !important;
        color: #fff !important;
        font-size: 14px !important;
        line-height: 1 !important;
        font-weight: 800 !important;
        letter-spacing: .3px !important;
        text-align: center !important;
        white-space: nowrap !important;
        cursor: pointer !important;
      }

      .sticky-add-to-cart__bar.horizon-final-sticky-cart
      .sticky-add-to-cart__button:hover {
        background: #FD7911 !important;
      }

      .sticky-add-to-cart__bar.horizon-final-sticky-cart
      .add-to-cart-text {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 100% !important;
      }

      .sticky-add-to-cart__bar.horizon-final-sticky-cart
      .add-to-cart-text > .svg-wrapper,
      .sticky-add-to-cart__bar.horizon-final-sticky-cart
      .add-to-cart__added {
        display: none !important;
      }

      .sticky-add-to-cart__bar.horizon-final-sticky-cart
      .add-to-cart-text__content {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 100% !important;
        color: #fff !important;
      }

      .sticky-add-to-cart__bar.horizon-final-sticky-cart
      .add-to-cart-text__content > span:first-child {
        display: inline !important;
        color: #fff !important;
        font-size: 14px !important;
        font-weight: 800 !important;
      }

      .sticky-add-to-cart__bar.horizon-final-sticky-cart
      [ref='quantityDisplay'] {
        display: none !important;
      }

      /* Professional shake ONLY for Buy Now and sticky ORDER NOW.
         Nothing here targets the normal Add to Cart button. */
      .horizon-final-buy-now-shake {
        transform-origin: center !important;
        animation: horizon-final-buy-now-shake 4.5s ease-in-out infinite !important;
        will-change: transform !important;
      }

      @keyframes horizon-final-buy-now-shake {
        0%, 72%, 100% {
          transform: translate3d(0, 0, 0) rotate(0deg);
        }
        74% {
          transform: translate3d(-2px, 0, 0) rotate(-0.5deg);
        }
        76% {
          transform: translate3d(2px, 0, 0) rotate(0.5deg);
        }
        78% {
          transform: translate3d(-2px, 0, 0) rotate(-0.45deg);
        }
        80% {
          transform: translate3d(2px, 0, 0) rotate(0.45deg);
        }
        82% {
          transform: translate3d(-1px, 0, 0) rotate(-0.2deg);
        }
        84% {
          transform: translate3d(1px, 0, 0) rotate(0.2deg);
        }
        86% {
          transform: translate3d(0, 0, 0) rotate(0deg);
        }
      }

      /* Scrollable space AFTER the complete footer/copyright */
      .horizon-final-footer-space {
        display: block !important;
        width: 100% !important;
        height: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }

      @media screen and (max-width: 749px) {
        .sticky-add-to-cart__bar.horizon-final-sticky-cart {
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          transform: translateY(calc(100% + 20px)) !important;
          width: 100% !important;
          max-width: none !important;
          grid-template-columns: 50px minmax(0, 1fr) !important;
          grid-template-rows: 35px 58px 44px !important;
          column-gap: 9px !important;
          row-gap: 8px !important;
          padding: 0 9px calc(9px + var(--safe-area-inset-bottom, 0px)) !important;
          border-radius: 14px 14px 0 0 !important;
        }

        .sticky-add-to-cart__bar.horizon-final-sticky-cart[data-stuck='true'] {
          transform: translateY(0) !important;
        }

        .horizon-final-sticky-offer {
          min-height: 35px !important;
          margin: 0 -9px !important;
          padding: 6px 9px !important;
          font-size: 11.5px !important;
        }

        .sticky-add-to-cart__bar.horizon-final-sticky-cart
        .sticky-add-to-cart__image {
          grid-column: 1 !important;
          grid-row: 2 !important;
          width: 50px !important;
          height: 50px !important;
          min-width: 50px !important;
          max-width: 50px !important;
          border-radius: 7px !important;
        }

        .sticky-add-to-cart__bar.horizon-final-sticky-cart
        .sticky-add-to-cart__info {
          grid-column: 2 !important;
          grid-row: 2 !important;
          min-width: 0 !important;
          gap: 5px !important;
          overflow: hidden !important;
          align-self: center !important;
        }

        .sticky-add-to-cart__bar.horizon-final-sticky-cart
        .sticky-add-to-cart__title {
          font-size: 14px !important;
          line-height: 1.1 !important;
          font-weight: 700 !important;
        }

        .horizon-final-sticky-price-row {
          gap: 5px !important;
          min-height: 17px !important;
        }

        .horizon-final-sticky-current-price {
          font-size: 16px !important;
        }

        .horizon-final-sticky-compare-price {
          font-size: 12px !important;
        }

        .horizon-final-sticky-discount {
          font-size: 11px !important;
        }

        .sticky-add-to-cart__bar.horizon-final-sticky-cart
        .sticky-add-to-cart__button {
          grid-column: 1 / -1 !important;
          grid-row: 3 !important;
          width: 100% !important;
          min-width: 0 !important;
          height: 44px !important;
          padding: 0 10px !important;
          font-size: 13px !important;
          justify-self: stretch !important;
        }

        .sticky-add-to-cart__bar.horizon-final-sticky-cart
        .add-to-cart-text__content > span:first-child {
          font-size: 13px !important;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .horizon-final-buy-now-shake {
          animation: none !important;
        }
      }
    `;

    document.head.appendChild(style);
  }

  #enhanceStickyBar() {
    const stickyBar = this.refs.stickyBar;
    if (!stickyBar) return;

    stickyBar.classList.add('horizon-final-sticky-cart');

    // Offer header.
    let offer = stickyBar.querySelector('[data-horizon-final-offer]');

    if (!offer) {
      offer = document.createElement('div');
      offer.className = 'horizon-final-sticky-offer';
      offer.setAttribute('data-horizon-final-offer', '');
      offer.textContent =
        'PREPAID Offer: Extra 5% off + Free Express Shipping 🙏';
      stickyBar.prepend(offer);
    }

    // Replace only the visible sticky label. Keep Horizon's refs intact.
    const label =
      this.refs.addToCartButton.querySelector(
        '.add-to-cart-text__content > span:first-child'
      );

    if (label) {
      label.textContent = 'ORDER NOW';
    }

    // Hide icon and native quantity text inside the sticky button.
    this.refs.addToCartButton
      .querySelector('.add-to-cart-text > .svg-wrapper')
      ?.setAttribute('aria-hidden', 'true');

    this.refs.addToCartButton
      .querySelector('.add-to-cart-text > .svg-wrapper')
      ?.style.setProperty('display', 'none');

    this.refs.addToCartButton
      .querySelector('[ref="quantityDisplay"]')
      ?.style.setProperty('display', 'none');

    this.refs.addToCartButton.classList.add(
      'horizon-final-buy-now-shake'
    );

    this.#updateDiscountBadge();
  }

  #updateDiscountBadge() {
    const stickyBar = this.refs.stickyBar;
    const priceBox = stickyBar?.querySelector(
      '[data-testid="sticky-price-display"]'
    );
    const info = stickyBar?.querySelector('.sticky-add-to-cart__info');
    if (!priceBox || !info) return;

    let priceRow = info.querySelector('[data-horizon-final-price-row]');

    if (!priceRow) {
      priceRow = document.createElement('div');
      priceRow.className = 'horizon-final-sticky-price-row';
      priceRow.setAttribute('data-horizon-final-price-row', '');
      info.appendChild(priceRow);
    }

    let current = priceRow.querySelector('[data-horizon-final-current-price]');
    let compare = priceRow.querySelector('[data-horizon-final-compare-price]');
    let discount = priceRow.querySelector('[data-horizon-final-discount]');

    if (!current) {
      current = document.createElement('span');
      current.className = 'horizon-final-sticky-current-price';
      current.setAttribute('data-horizon-final-current-price', '');
      priceRow.appendChild(current);
    }

    if (!compare) {
      compare = document.createElement('span');
      compare.className = 'horizon-final-sticky-compare-price';
      compare.setAttribute('data-horizon-final-compare-price', '');
      priceRow.appendChild(compare);
    }

    if (!discount) {
      discount = document.createElement('span');
      discount.className = 'horizon-final-sticky-discount';
      discount.setAttribute('data-horizon-final-discount', '');
      priceRow.appendChild(discount);
    }

    // Read the actual bdi text so accessibility labels such as
    // "Regular price" never leak into the visible sticky bar.
    const currentPrice = priceBox.querySelector(
      '.price-item__group.price bdi, .price bdi'
    );

    let comparePrice = priceBox.querySelector(
      '.price-item__group.price-item--regular.compare-at-price bdi, .price-item__group.compare-at-price bdi, .compare-at-price bdi, s bdi, del bdi, [class*="compare" i] bdi'
    );

    // Fallback: with two bdi values the second one is the compare-at value
    // in the sale-first layout.
    if (!comparePrice) {
      const bdis = Array.from(priceBox.querySelectorAll('bdi'));
      if (bdis.length > 1) comparePrice = bdis[1];
    }

    const currentText = currentPrice?.textContent?.trim() || '';
    let compareText = comparePrice?.textContent?.trim() || '';

    // Fallback: read the compare-at price shown in the main product price.
    if (!compareText) {
      const section = this.closest('.shopify-section');
      const mainCompare = Array.from(
        section?.querySelectorAll(
          '.compare-at-price bdi, s bdi, del bdi, [class*="compare" i] bdi'
        ) ?? []
      ).find(
        (el) => !el.closest('sticky-add-to-cart') && !el.closest('product-card')
      );
      compareText = mainCompare?.textContent?.trim() || '';
    }

    const parseMoney = (value) => {
      const match = String(value || '').match(/\d[\d.,\s]*/);
      if (!match) return 0;

      let normalized = match[0].replace(/\s/g, '');
      const lastComma = normalized.lastIndexOf(',');
      const lastDot = normalized.lastIndexOf('.');

      if (lastComma > -1 && lastDot > -1) {
        normalized =
          lastComma > lastDot
            ? normalized.replace(/\./g, '').replace(',', '.')
            : normalized.replace(/,/g, '');
      } else if (lastComma > -1) {
        normalized = /,\d{1,2}$/.test(normalized)
          ? normalized.replace(',', '.')
          : normalized.replace(/,/g, '');
      }

      const number = parseFloat(normalized);
      return Number.isFinite(number) ? number : 0;
    };

    const price = parseMoney(currentText);
    const compareValue = parseMoney(compareText);

    // Show the struck-out compare-at price whenever it differs from the
    // selling price (and is higher, when both can be read as numbers).
    const showCompare =
      Boolean(compareText) &&
      compareText !== currentText &&
      (price === 0 || compareValue === 0 || compareValue > price);

    current.textContent = currentText;

    if (showCompare) {
      compare.textContent = compareText;
      compare.style.display = 'inline';
    } else {
      compare.textContent = '';
      compare.style.display = 'none';
    }

    if (price > 0 && compareValue > price) {
      discount.textContent =
        `${Math.round(((compareValue - price) / compareValue) * 100)}% Off`;
      discount.style.display = 'inline';
    } else {
      discount.textContent = '';
      discount.style.display = 'none';
    }
  }

  #updateBuyNowAnimation() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock =
      productForm.closest('.buy-buttons-block');

    if (!buyButtonsBlock) return;

    const accelerated =
      buyButtonsBlock.querySelector(
        '.accelerated-checkout-block'
      );

    if (!accelerated) return;

    const buyNow =
      accelerated.querySelector(
        'shopify-accelerated-checkout'
      ) ||
      accelerated.querySelector(
        '.shopify-payment-button'
      ) ||
      accelerated.querySelector(
        '.shopify-payment-button__button'
      );

    buyNow?.classList.add(
      'horizon-final-buy-now-shake'
    );

    // Explicitly ensure the normal Add to Cart button never gets
    // our animation class.
    productForm
      .querySelector('[ref="addToCartButton"]')
      ?.classList.remove(
        'horizon-final-buy-now-shake'
      );
  }

  #updateFooterScrollSpace() {
    let spacer = document.getElementById(
      'horizon-final-footer-space'
    );

    if (!spacer) {
      spacer = document.createElement('div');
      spacer.id = 'horizon-final-footer-space';
      spacer.className = 'horizon-final-footer-space';
      document.body.appendChild(spacer);
    }

    // Also add real bottom padding to the actual footer. This is more
    // reliable than relying on a body child when the theme footer uses its
    // own layout/overflow rules. The copyright therefore remains above the
    // fixed sticky bar at the true bottom of the page.
    const footer = document.querySelector(
      'footer, #shopify-section-footer, .footer'
    );

    if (!this.#isStuck) {
      spacer.style.height = '0px';
      footer?.style.removeProperty('padding-bottom');
      footer?.style.removeProperty('margin-bottom');
      return;
    }

    const stickyHeight = Math.ceil(
      this.refs.stickyBar.getBoundingClientRect().height
    );
    const clearance = stickyHeight + 28;

    spacer.style.height = `${clearance}px`;

    if (footer) {
      footer.style.setProperty(
        'padding-bottom',
        `${clearance}px`,
        'important'
      );
    }
  }

  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Finds the main product page Buy Now / express checkout element
   * (PenguinCOD "Buy It Now" or the native accelerated checkout button).
   * Never returns anything inside the sticky bar itself.
   * @returns {Element | null}
   */
  #findMainBuyNowTarget() {
    const productForm = this.#getProductForm();
    const scope = productForm?.closest('.shopify-section') ?? document;

    // If you know the exact PenguinCOD button selector, add it at the top.
    const selectors = [
      '[class*="penguin" i]',
      '[id*="penguin" i]',
      '[data-penguin]',
      '.accelerated-checkout-block',
      'shopify-accelerated-checkout',
      '.shopify-payment-button',
      '.shopify-payment-button__button',
      '.buy-now-button',
    ];

    for (const selector of selectors) {
      const found = Array.from(scope.querySelectorAll(selector)).find(
        (el) => !el.closest('sticky-add-to-cart')
      );
      if (found) return found;
    }

    // Fallback: match by visible button text
    const buttons = scope.querySelectorAll(
      'button, a[role="button"], input[type="button"], input[type="submit"]'
    );

    const byText = Array.from(buttons).find((btn) => {
      if (btn.closest('sticky-add-to-cart')) return false;
      const text = (btn.textContent || btn.value || '').trim().toLowerCase();
      return /^(buy it now|buy now)$/.test(text);
    });

    if (byText) return byText;

    // Last resort: the whole buy buttons block / product form
    return productForm?.closest('.buy-buttons-block') ?? productForm ?? null;
  }

  /**
   * Smoothly scrolls the page to the main Buy Now button.
   */
  #scrollToMainBuyNow() {
    const target = this.#findMainBuyNowTarget();
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}